import { Injectable } from "@angular/core";
import { BaseValidationResult } from "@ha/ui/forms";
import { Navigate } from "@ngxs/router-plugin";
import { Action, State, StateContext, Store } from "@ngxs/store";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

import { PermitPaths } from "../../permit-paths";
import { PermitRequest } from "../../shared/models/permit-request.model";
import { PermitListItem } from "../models/permit-list-item.model";
import { PermitWorkplaceItem } from "../models/permit-workplace-item.model";
import { PreparedPermitListItem } from "../models/prepared-permit-list-item.model";
import { PermitListService } from "../services/permit-list.service";
import { PermitListActions } from "./permit-list.actions";
import { PermitListSelectors } from "./permit-list.selectors";

export interface PermitListStateModel {
    permits: PermitListItem[] | undefined;
    preparedPermits: PreparedPermitListItem[] | undefined;
    retrievableFlammableAtexPermits: PreparedPermitListItem[] | undefined;
    ongoingPermits: PermitListItem[] | undefined;
    filteredPermits: PermitListItem[] | undefined;
    plannedPermits: PermitListItem[] | undefined;
    completedPermits: PermitListItem[] | undefined;
    selectedWorkplaces: string[] | undefined;
    workplaces: PermitWorkplaceItem[] | undefined;
    showAllSubscriptionPermits?: boolean;
    loading?: boolean;
    hasMoreCompletedPermits?: boolean;
    retrievableFlammableAtexPermitConnected?: boolean;
    retrievableFlammableAtexPermitErrorMessage?: string;
}

@Injectable()
@State<PermitListStateModel>({
    name: "permitList",
    defaults: {
        permits: undefined,
        preparedPermits: undefined,
        retrievableFlammableAtexPermits: undefined,
        ongoingPermits: undefined,
        filteredPermits: undefined,
        plannedPermits: undefined,
        completedPermits: undefined,
        selectedWorkplaces: undefined,
        workplaces: undefined,
        showAllSubscriptionPermits: false,
        hasMoreCompletedPermits: false,
        retrievableFlammableAtexPermitConnected: false,
        retrievableFlammableAtexPermitErrorMessage: undefined,
    },
})
export class PermitListState {
    constructor(private store: Store, private permitListService: PermitListService) {}

    @Action(PermitListActions.GetAllCompleted)
    public getAllCompleted(
        ctx: StateContext<PermitListStateModel>,
        action: PermitListActions.GetAllCompleted,
    ): Observable<PermitListItem[]> {
        return this.permitListService.getAllCompleted(action.skip, action.take).pipe(tap((data) => {
            const currentState = ctx.getState();
            const currentCompletedPermits = currentState.completedPermits || [];

            const newCompletedPermits = action.skip === 0 ? data : [...currentCompletedPermits, ...data];

            const hasMoreCompletedPermits = data.length === action.take;

            ctx.patchState({
                completedPermits: newCompletedPermits,
                hasMoreCompletedPermits: hasMoreCompletedPermits,
            });
        }));
    }

    @Action(PermitListActions.GetAllOngoing)
    public getAllOngoing(
        ctx: StateContext<PermitListStateModel>,
        action: PermitListActions.GetAllOngoing,
    ): Observable<PermitListItem[]> {
        var includeAll = ctx.getState().showAllSubscriptionPermits;
        if (includeAll == undefined) {
            includeAll = false;
        }
        ctx.patchState({
            loading: true,
        });
        return this.permitListService.getAllOngoing(includeAll).pipe(tap((data) => {
            var wps = this.store.selectSnapshot(PermitListSelectors.workplaces).filter((item) => item.isSelected).map((
                item,
            ) => item.name);
            ctx.patchState({
                ongoingPermits: data,
                selectedWorkplaces: wps,
                filteredPermits: wps.length == 0
                    ? data
                    : data?.filter((item) => wps.indexOf(item.workplaceContainer) > -1),
                loading: false,
            });
        }));
    }

    @Action(PermitListActions.ToggleShowAllPermits)
    public toggleShowAllPermits(
        ctx: StateContext<PermitListStateModel>,
        action: PermitListActions.ToggleShowAllPermits,
    ): void {
        var includeAll = this.store.selectSnapshot(PermitListSelectors.showAllSubscriptionPermits);
        ctx.patchState({
            showAllSubscriptionPermits: !includeAll,
        });
    }

    @Action(PermitListActions.UpdateFilter)
    public updateFilter(ctx: StateContext<PermitListStateModel>, action: PermitListActions.UpdateFilter): void {
        var wps = this.store.selectSnapshot(PermitListSelectors.workplaces).map((item) => ({
            name: item.name,
            availablePermits: item.availablePermits,
            isSelected: action.value.indexOf(item.name) > -1,
        }));

        var data = this.store.selectSnapshot(PermitListSelectors.ongoingPermits);
        var selected = wps.filter((item) => item.isSelected).map((item) => item.name);

        ctx.patchState({
            workplaces: wps,
            selectedWorkplaces: action.value,
            filteredPermits: selected.length == 0
                ? data
                : data?.filter((item) => selected.indexOf(item.workplaceContainer) > -1),
        });
    }

    @Action(PermitListActions.GetAllPlanned)
    public getAllPlanned(ctx: StateContext<PermitListStateModel>): Observable<PermitListItem[]> {
        ctx.patchState({
            loading: true,
        });
        return this.permitListService.getAllPlanned().pipe(tap((data) => {
            ctx.patchState({
                plannedPermits: data,
                loading: false,
            });
        }));
    }

    @Action(PermitListActions.GetAllPrepared)
    public getAllPrepared(ctx: StateContext<PermitListStateModel>): Observable<PreparedPermitListItem[]> {
        ctx.patchState({
            loading: true,
        });
        return this.permitListService.getAllPrepared().pipe(tap((data) => {
            ctx.patchState({
                preparedPermits: data,
                loading: false,
            });
        }));
    }

    @Action(PermitListActions.GetRetrievableFlammableAtexPermits)
    public getRetrievableFlammableAtexPermits(
        ctx: StateContext<PermitListStateModel>,
        action: PermitListActions.GetRetrievableFlammableAtexPermits,
    ): Observable<PreparedPermitListItem[]> {
        ctx.patchState({
            loading: true,
            retrievableFlammableAtexPermitConnected: false,
            retrievableFlammableAtexPermitErrorMessage: undefined,
        });

        return this.permitListService.getRetrievableFlammableAtexPermits(action.permitId).pipe(tap((data) => {
            ctx.patchState({
                retrievableFlammableAtexPermits: data,
                loading: false,
            });
        }));
    }

    @Action(PermitListActions.ClaimPreparedPermit)
    public claimPreparedPermit(
        ctx: StateContext<PermitListStateModel>,
        action: PermitListActions.ClaimPreparedPermit,
    ): Observable<BaseValidationResult> {
        ctx.patchState({
            loading: true,
        });
        return this.permitListService.claimPreparedPermit(action.permitId).pipe(tap((result) => {
            if (result.success) {
                this.store.dispatch(new Navigate([PermitPaths.Root, action.permitId]));
            }
            ctx.patchState({
                loading: false,
            });
        }));
    }

    @Action(PermitListActions.ConnectStandaloneFlammableAtexPermit)
    public connectStandaloneFlammableAtexPermit(
        ctx: StateContext<PermitListStateModel>,
        action: PermitListActions.ConnectStandaloneFlammableAtexPermit,
    ): Observable<BaseValidationResult> {
        ctx.patchState({
            loading: true,
            retrievableFlammableAtexPermitConnected: false,
            retrievableFlammableAtexPermitErrorMessage: undefined,
        });

        return this.permitListService.connectStandaloneFlammableAtexPermit(
            action.hotWorkPermitId,
            action.flammableAtexPermitId,
        ).pipe(tap((result) => {
            ctx.patchState({
                loading: false,
                retrievableFlammableAtexPermitConnected: result?.success === true,
                retrievableFlammableAtexPermitErrorMessage: result?.success === true
                    ? undefined
                    : result?.validationResult?.errors?.[0]?.errorMessage,
            });
        }));
    }

    @Action(PermitListActions.RemovePlannedPermit)
    public removePlannedPermit(
        ctx: StateContext<PermitListStateModel>,
        action: PermitListActions.RemovePlannedPermit,
    ): Observable<BaseValidationResult> {
        ctx.patchState({
            loading: true,
        });
        return this.permitListService.removePlannedPermit(action.permitId).pipe(tap((result) => {
            if (result.success) {
                // Success
            }
            ctx.patchState({
                loading: false,
            });
        }));
    }
}
